<template>
  <div class="order-create-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">‹ 返回</button>
      <div class="page-title">确认订单</div>
    </div>

    <!-- 产品信息 -->
    <div class="order-section">
      <div class="section-title">产品信息</div>
      <div class="info-card">
        <div class="info-row">
          <div class="info-label">产品</div>
          <div class="info-value">{{ productInfo.productName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">规格</div>
          <div class="info-value">{{ productInfo.skuName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">出行日期</div>
          <div class="info-value">{{ productInfo.date }}</div>
        </div>
        <div v-for="(item, index) in priceItems" :key="index" class="info-row">
          <div class="info-label">{{ item.priceName }} × {{ item.quantity }}</div>
          <div class="info-value price-text">¥{{ itemSubtotal(item) }}</div>
        </div>
        <div class="info-row total">
          <div class="info-label">小计</div>
          <div class="info-value price-text">¥{{ orderAmount }}</div>
        </div>
      </div>
    </div>

    <!-- 出行人选择 -->
    <div class="order-section">
      <div class="section-title">出行人（需选择 {{ totalTravelerCount }} 位，已选 {{ selectedTravelers.length }} 位）</div>
      <div class="traveler-list" v-if="travelers.length > 0">
        <div
          v-for="traveler in travelers"
          :key="traveler.id"
          class="traveler-item"
          :class="{ selected: selectedTravelers.includes(traveler.id) }"
          @click="toggleTraveler(traveler.id)"
        >
          <div class="traveler-checkbox">
            <span v-if="selectedTravelers.includes(traveler.id)">✓</span>
          </div>
          <div class="traveler-info">
            <div class="traveler-name">{{ traveler.name }}</div>
            <div class="traveler-id">{{ traveler.idCard }}</div>
          </div>
        </div>
      </div>
      <div v-else class="no-traveler">暂无出行人，请先添加</div>
      <button class="add-traveler-btn" @click="openTravelerSelector">+ 选择或添加出行人</button>
    </div>

    <!-- 联系人信息 -->
    <div class="order-section">
      <div class="section-title-row">
        <div class="section-title">联系人</div>
        <button v-if="travelers.length > 0" class="pick-btn" @click="showTravelerSelector = true">从出行人中选择</button>
      </div>
      <div class="form-group">
        <label class="form-label">姓名</label>
        <input v-model="contactInfo.name" type="text" class="form-input" placeholder="请输入联系人姓名" />
      </div>
      <div class="form-group">
        <label class="form-label">手机号</label>
        <input v-model="contactInfo.phone" type="tel" class="form-input" placeholder="请输入联系人手机号" />
      </div>
    </div>

    <!-- 优惠券 -->
    <div class="order-section">
      <div class="section-title-row">
        <div class="section-title">优惠券</div>
        <div class="coupon-trigger" @click="openCouponPicker">
          <span v-if="selectedCoupon" class="coupon-selected">-¥{{ selectedCoupon.discountAmount }}  更换</span>
          <span v-else class="coupon-tip">{{ availableCoupons.length > 0 ? availableCoupons.length + '张可用' : '暂无可用' }} ›</span>
        </div>
      </div>
    </div>

    <!-- 备注 -->
    <div class="order-section">
      <div class="section-title">备注（选填）</div>
      <textarea v-model="remark" class="form-textarea" placeholder="请输入订单备注" rows="3"></textarea>
    </div>

    <!-- 费用汇总 -->
    <div class="order-section">
      <div class="fee-row">
        <div class="fee-label">订单金额</div>
        <div class="fee-val">¥{{ orderAmount }}</div>
      </div>
      <div v-if="selectedCoupon" class="fee-row">
        <div class="fee-label">优惠券</div>
        <div class="fee-val discount">-¥{{ selectedCoupon.discountAmount }}</div>
      </div>
      <div class="fee-row total">
        <div class="fee-label">实付金额</div>
        <div class="fee-val total-price">¥{{ payAmount }}</div>
      </div>
    </div>

    <!-- 底部操作 -->
    <div class="order-footer">
      <div class="footer-price">¥{{ payAmount }}</div>
      <button class="submit-btn" :disabled="!canSubmit || loading" @click="handleConfirm">
        {{ loading ? '提交中...' : '提交订单' }}
      </button>
    </div>

    <!-- 联系人选择弹窗 -->
    <div v-if="showTravelerSelector" class="modal-overlay" @click.self="showTravelerSelector = false">
      <div class="modal-sheet">
        <div class="modal-header">
          <div class="modal-title">选择联系人</div>
          <button class="modal-close" @click="showTravelerSelector = false">✕</button>
        </div>
        <div class="picker-list">
          <div v-for="t in travelers" :key="t.id" class="picker-item" @click="selectTravelerAsContact(t)">
            <div class="picker-name">{{ t.name }}</div>
            <div class="picker-sub">{{ t.phone || t.idCard }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 优惠券选择弹窗 -->
    <div v-if="showCouponPicker" class="modal-overlay" @click.self="showCouponPicker = false">
      <div class="modal-sheet">
        <div class="modal-header">
          <div class="modal-title">选择优惠券</div>
          <button class="modal-close" @click="showCouponPicker = false">✕</button>
        </div>
        <div class="coupon-list">
          <div
            class="coupon-item no-coupon-opt"
            :class="{ active: !selectedCoupon }"
            @click="selectCoupon(null)"
          >不使用优惠券</div>
          <div
            v-for="coupon in availableCoupons"
            :key="coupon.id"
            class="coupon-item"
            :class="{ active: selectedCoupon && selectedCoupon.id === coupon.id }"
            @click="selectCoupon(coupon)"
          >
            <div class="coupon-amount">-¥{{ coupon.discountAmount }}</div>
            <div class="coupon-info">
              <div class="coupon-name">{{ coupon.couponName }}</div>
              <div class="coupon-expire">有效期至 {{ formatDate(coupon.expireTime) }}</div>
            </div>
          </div>
          <div v-if="availableCoupons.length === 0" class="no-coupon-tip">暂无可用优惠券</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getTravelerList } from '@/api/user'
import { createOrder } from '@/api/order'
import { getAvailableCoupons } from '@/api/coupon'
import { getProductDetail } from '@/api/browse'
import { formatDate } from '@/utils/format'

const TRAVELER_SELECTION_KEY = 'sweetour_order_traveler_selection'

export default {
  name: 'OrderCreate',
  data() {
    return {
      productInfo: { productId: '', productName: '', skuId: '', skuName: '', date: '', calendarPriceId: '' },
      priceItems: [],
      travelers: [],
      selectedTravelers: [],
      contactInfo: { name: '', phone: '' },
      remark: '',
      availableCoupons: [],
      selectedCoupon: null,
      loading: false,
      showTravelerSelector: false,
      showCouponPicker: false
    }
  },
  computed: {
    // 金额一律按整数分累加，避免浮点乘加误差；仅用于展示/传参时才转换回元
    totalTravelerCount() {
      return this.priceItems.reduce((s, i) => s + (i.quantity || 0), 0)
    },
    orderAmountCents() {
      return this.priceItems.reduce((s, i) => s + Math.round(i.price * 100) * i.quantity, 0)
    },
    orderAmount() {
      return (this.orderAmountCents / 100).toFixed(2)
    },
    payAmount() {
      const discountCents = this.selectedCoupon ? Math.round(this.selectedCoupon.discountAmount * 100) : 0
      return (Math.max(0, this.orderAmountCents - discountCents) / 100).toFixed(2)
    },
    canSubmit() {
      return (
        this.totalTravelerCount > 0 &&
        this.selectedTravelers.length === this.totalTravelerCount &&
        this.contactInfo.name.trim() &&
        this.contactInfo.phone.trim()
      )
    }
  },
  async mounted() {
    const q = this.$route.query
    this.productInfo.productId = q.productId
    this.productInfo.skuId = q.skuId
    this.productInfo.date = q.date
    this.productInfo.calendarPriceId = q.calendarPriceId
    try { this.priceItems = JSON.parse(decodeURIComponent(q.priceItems || '[]')) } catch { this.priceItems = [] }
    await this.loadTravelers()
    this.consumeTravelerSelection()
    await this.loadProductName()
    await this.loadCoupons()
  },
  methods: {
    async loadTravelers() {
      try { const res = await getTravelerList(); this.travelers = res.data || [] } catch (e) { console.error(e) }
    },
    async loadProductName() {
      if (!this.productInfo.productId) return
      try {
        const res = await getProductDetail(this.productInfo.productId)
        const product = res.data?.product || res.data || {}
        this.productInfo.productName = product.title || ''
        const skuResult = res.data?.skus || []
        const skuEntry = skuResult.find(item => String((item.sku || item).id) === String(this.productInfo.skuId))
        this.productInfo.skuName = skuEntry ? (skuEntry.sku || skuEntry).title : ''
      } catch (e) { console.error(e) }
    },
    async loadCoupons() {
      try {
        const res = await getAvailableCoupons(this.productInfo.productId, parseFloat(this.orderAmount))
        this.availableCoupons = res.data || []
      } catch (e) { console.error(e) }
    },
    openCouponPicker() {
      if (this.availableCoupons.length === 0 && !this.selectedCoupon) {
        this.$alert('暂无可用优惠券')
        return
      }
      this.showCouponPicker = true
    },
    toggleTraveler(id) {
      const idx = this.selectedTravelers.indexOf(id)
      if (idx > -1) this.selectedTravelers.splice(idx, 1)
      else if (this.selectedTravelers.length < this.totalTravelerCount) this.selectedTravelers.push(id)
      else this.$alert(`本订单只需选择 ${this.totalTravelerCount} 位出行人`)
    },
    openTravelerSelector() {
      this.$router.push({
        name: 'Traveler',
        query: {
          mode: 'select',
          requiredCount: this.totalTravelerCount,
          selectedIds: this.selectedTravelers.join(',')
        }
      })
    },
    consumeTravelerSelection() {
      try {
        const raw = sessionStorage.getItem(TRAVELER_SELECTION_KEY)
        if (!raw) return
        sessionStorage.removeItem(TRAVELER_SELECTION_KEY)
        const selectedIds = JSON.parse(raw)
        const availableIds = new Set(this.travelers.map(item => Number(item.id)))
        this.selectedTravelers = (Array.isArray(selectedIds) ? selectedIds : [])
          .map(Number)
          .filter(id => availableIds.has(id))
          .slice(0, this.totalTravelerCount)
      } catch (error) {
        sessionStorage.removeItem(TRAVELER_SELECTION_KEY)
        console.error('读取出行人选择结果失败:', error)
      }
    },
    selectTravelerAsContact(t) {
      this.contactInfo.name = t.name
      this.contactInfo.phone = t.phone || ''
      this.showTravelerSelector = false
    },
    selectCoupon(coupon) {
      this.selectedCoupon = coupon
      this.showCouponPicker = false
    },
    async handleConfirm() {
      if (this.selectedTravelers.length !== this.totalTravelerCount) {
        this.$alert(`请选择 ${this.totalTravelerCount} 位出行人（当前已选 ${this.selectedTravelers.length} 位）`)
        return
      }
      if (!this.canSubmit) { this.$alert('请完整填写出行人和联系人信息'); return }
      this.loading = true
      try {
        const travelers = this.selectedTravelers.map(id => {
          const t = this.travelers.find(x => x.id === id)
          return { name: t?.name || '', phone: t?.phone || '', idCard: t?.idCard || '', passport: t?.passport || '' }
        })
        const res = await createOrder({
          productId: parseInt(this.productInfo.productId) || 0,
          skuId: parseInt(this.productInfo.skuId) || 0,
          calendarPriceId: parseInt(this.productInfo.calendarPriceId) || 0,
          priceItems: this.priceItems.map(i => ({ priceItemId: i.priceItemId, quantity: i.quantity })),
          travelDate: this.productInfo.date,
          contactName: this.contactInfo.name,
          contactPhone: this.contactInfo.phone,
          userRemark: this.remark,
          travelers,
          userCouponId: this.selectedCoupon ? this.selectedCoupon.id : null
        })
        const orderId = res.data?.orderId || res.data
        this.$router.replace({ name: 'OrderDetail', params: { id: orderId } })
      } catch (error) {
        console.error('创建订单失败:', error)
        this.$alert(error.message || '创建订单失败，请重试')
      } finally {
        this.loading = false
      }
    },
    itemSubtotal(item) {
      return ((Math.round(item.price * 100) * item.quantity) / 100).toFixed(2)
    },
    formatDate
  }
}
</script>

<style lang="scss" scoped>
.order-create-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

.page-header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 20;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #ff720d;
  cursor: pointer;
  margin-right: 12px;
  padding: 0;
}

.page-title { font-size: 16px; font-weight: 600; color: #111827; }

.order-section {
  background: #fff;
  margin: 10px 0;
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.pick-btn {
  background: #fff0e3;
  color: #ff720d;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.info-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  &.total { font-weight: 600; padding-top: 10px; }
}

.info-label { font-size: 13px; color: #6b7280; }
.info-value { font-size: 13px; color: #111827; font-weight: 500; text-align: right; }
.price-text { color: #ff720d; }

.traveler-list { margin-bottom: 12px; display: flex; flex-direction: column; gap: 8px; }
.traveler-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  &.selected { background: #fff0e3; border-color: #ff720d; }
}
.traveler-checkbox {
  width: 20px; height: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  margin-right: 12px;
  color: #ff720d; font-weight: 700; font-size: 12px;
}
.traveler-info { flex: 1; }
.traveler-name { font-size: 14px; font-weight: 600; color: #111827; }
.traveler-id { font-size: 12px; color: #6b7280; margin-top: 2px; }
.no-traveler { font-size: 13px; color: #6b7280; margin-bottom: 12px; }

.add-traveler-btn {
  width: 100%;
  padding: 10px;
  background: transparent;
  color: #ff720d;
  border: 1px dashed #ff720d;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.form-group { margin-bottom: 14px; }
.form-label { display: block; font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 6px; }
.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff720d; }
}
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  outline: none;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  &:focus { border-color: #ff720d; }
}

.coupon-trigger { cursor: pointer; }
.coupon-selected { font-size: 14px; font-weight: 600; color: #10b981; }
.coupon-tip { font-size: 13px; color: #ff720d; }

.fee-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  &.total { border-top: 1px solid #e5e7eb; margin-top: 8px; padding-top: 12px; }
}
.fee-label { font-size: 13px; color: #6b7280; }
.fee-val { font-size: 13px; color: #111827; font-weight: 500;
  &.discount { color: #10b981; }
}
.total-price { font-size: 18px; font-weight: 700; color: #ff720d; }

.order-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  z-index: 10;
}
.footer-price { font-size: 20px; font-weight: 700; color: #ff720d; }
.submit-btn {
  padding: 12px 32px;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):active { opacity: 0.85; }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 100;
  display: flex;
  align-items: flex-end;
}
.modal-sheet {
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}
.modal-title { font-size: 16px; font-weight: 600; color: #111827; }
.modal-close { background: none; border: none; font-size: 20px; color: #6b7280; cursor: pointer; padding: 0; }

.picker-list { display: flex; flex-direction: column; gap: 8px; }
.picker-item {
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  &:active { background: #fff0e3; border-color: #ff720d; }
}
.picker-name { font-size: 14px; font-weight: 600; color: #111827; }
.picker-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }

.coupon-list { display: flex; flex-direction: column; gap: 10px; }
.coupon-item {
  display: flex;
  align-items: center;
  padding: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s;
  &.active { border-color: #ff720d; background: #fff0e3; }
  &.no-coupon-opt { justify-content: center; font-size: 14px; color: #6b7280; font-weight: 500; }
}
.coupon-amount { font-size: 22px; font-weight: 700; color: #ef4444; margin-right: 14px; min-width: 60px; }
.coupon-info { flex: 1; }
.coupon-name { font-size: 14px; font-weight: 600; color: #111827; }
.coupon-expire { font-size: 12px; color: #6b7280; margin-top: 2px; }
.no-coupon-tip { text-align: center; font-size: 14px; color: #9ca3af; padding: 20px 0; }
</style>
